import { apiPost } from '@/utils/api';

// Session ID stored in memory (lost on page refresh)
let currentSessionId: string | null = null;
let sessionStartTime: number | null = null;

// Device and browser detection
function getDeviceInfo() {
  const ua = navigator.userAgent;

  let deviceType = 'desktop';
  if (/tablet|ipad|playbook|silk/i.test(ua)) {
    deviceType = 'tablet';
  } else if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) {
    deviceType = 'mobile';
  }

  let browser = 'Unknown';
  if (ua.indexOf('Firefox') > -1) browser = 'Firefox';
  else if (ua.indexOf('SamsungBrowser') > -1) browser = 'Samsung';
  else if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) browser = 'Opera';
  else if (ua.indexOf('Trident') > -1) browser = 'IE';
  else if (ua.indexOf('Edge') > -1) browser = 'Edge';
  else if (ua.indexOf('Edg') > -1) browser = 'Edge';
  else if (ua.indexOf('Chrome') > -1) browser = 'Chrome';
  else if (ua.indexOf('Safari') > -1) browser = 'Safari';

  let os = 'Unknown';
  if (ua.indexOf('Win') > -1) os = 'Windows';
  else if (ua.indexOf('Mac') > -1) os = 'macOS';
  else if (ua.indexOf('Linux') > -1) os = 'Linux';
  else if (ua.indexOf('Android') > -1) os = 'Android';
  else if (ua.indexOf('iOS') > -1 || ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) os = 'iOS';

  return { deviceType, browser, os };
}

// Start a new session
export async function startSession(): Promise<string | null> {
  try {
    const deviceInfo = getDeviceInfo();
    const response = await apiPost<{ sessionId: string }>('/analytics/track/session/start', deviceInfo);
    currentSessionId = response.sessionId;
    sessionStartTime = Date.now();
    return currentSessionId;
  } catch (error) {
    console.error('Failed to start tracking session:', error);
    return null;
  }
}

// End the current session
export async function endSession(): Promise<void> {
  if (!currentSessionId) return;

  try {
    await apiPost('/analytics/track/session/end', {
      sessionId: currentSessionId,
    });
    currentSessionId = null;
    sessionStartTime = null;
  } catch (error) {
    console.error('Failed to end tracking session:', error);
  }
}

// Get or create session ID
export async function getSessionId(): Promise<string | null> {
  if (!currentSessionId) {
    await startSession();
  }
  return currentSessionId;
}

// Track a page view
export async function trackPageView(pageUrl?: string): Promise<void> {
  const sessionId = await getSessionId();
  if (!sessionId) return;

  try {
    await apiPost('/analytics/track/pageview', {
      sessionId,
      pageUrl: pageUrl || window.location.href,
    });
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
}

// Track feature usage
export async function trackFeatureUse(
  featureName: string,
  metadata?: Record<string, unknown>
): Promise<void> {
  const sessionId = await getSessionId();
  if (!sessionId) return;

  try {
    await apiPost('/analytics/track/feature', {
      sessionId,
      featureName,
      metadata,
    });
  } catch (error) {
    console.error('Failed to track feature use:', error);
  }
}

// Feature tracking constants
export const FEATURES = {
  // Academic
  GRADES_VIEW: 'grades.view',
  GRADES_EDIT: 'grades.edit',
  ATTENDANCE_VIEW: 'attendance.view',
  ATTENDANCE_MARK: 'attendance.mark',
  SCHEDULE_VIEW: 'schedule.view',

  // Financial
  BILLING_VIEW: 'billing.view',
  PAYMENT_RECORD: 'payment.record',
  INVOICE_GENERATE: 'invoice.generate',

  // Communication
  EMAIL_CAMPAIGN_CREATE: 'email.campaign.create',
  EMAIL_CAMPAIGN_SEND: 'email.campaign.send',

  // Reports
  REPORT_GENERATE: 'report.generate',
  REPORT_EXPORT: 'report.export',

  // Admin
  USER_CREATE: 'user.create',
  USER_EDIT: 'user.edit',
  SETTINGS_UPDATE: 'settings.update',

  // CRM
  LEAD_CREATE: 'lead.create',
  LEAD_CONVERT: 'lead.convert',
  LEAD_CALL: 'lead.call',
};

// Initialize tracking on app load
export function initializeTracking(): void {
  // Start session on load
  startSession();

  // End session on page unload
  window.addEventListener('beforeunload', () => {
    // Use sendBeacon for reliability
    if (currentSessionId) {
      navigator.sendBeacon(
        '/api/analytics/track/session/end',
        JSON.stringify({ sessionId: currentSessionId })
      );
    }
  });

  // Track page views on route changes
  // This should be called from the router
}

// Hook for React Router to track page views
export function usePageTracking(): void {
  // This can be used with useEffect in a route wrapper
  trackPageView();
}

// Get current session duration in seconds
export function getSessionDuration(): number {
  if (!sessionStartTime) return 0;
  return Math.floor((Date.now() - sessionStartTime) / 1000);
}
